import { useContext, useMemo } from 'react';
import AvailabilityErrorReason from '~/constants/availabilityErrorReason';
import states from '~/constants/states';
import { useUserLocation } from '~/context/common/UserContext';
import { useIsDealershipPage } from '~/context/dealership/StaticContext';
import UserVehicleContext, {
  useVehicleAvailability
} from '~/context/vdp/UserVehicleContext';
import VehicleContext from '~/context/vdp/VehicleContext';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';
import useIsInExclusivePreview from '~/hooks/vdp/selectors/useIsInExclusivePreview';
import useIsWipVehicleReservedByUser from '~/hooks/vdp/selectors/useIsWipVehicleReservedByUser';

export default function useGetCtaWrapperContent() {
  const {
    similarVehicleCount: { data: similarVehicleCount, hasError, isLoaded }
  } = useContext(VehicleContext);
  const { isSubscribedToNotifications } = useContext(UserVehicleContext);
  const { isSold, otherUnavailable, purchasePending, unavailable } =
    useAlertStatus();
  const isDealership = useIsDealershipPage();
  const isInExclusivePreview = useIsInExclusivePreview();
  const isWipReservedByUser = useIsWipVehicleReservedByUser();
  const { state = '' } = useUserLocation();
  const { errorReason = '' } = useVehicleAvailability();
  const shouldShowDontFret = isLoaded && !hasError && similarVehicleCount > 0;
  const stateName = states[state] ?? state;

  const content = useMemo(() => {
    switch (true) {
      case isSold:
        return {
          title: 'This vehicle is no longer available',
          description: shouldShowDontFret
            ? 'Don’t fret, we’ve got many options to choose from.'
            : ''
        };
      case otherUnavailable:
        return {
          title: 'This vehicle is not available for purchase',
          description: shouldShowDontFret
            ? 'Don’t fret, we’ve got many options to choose from.'
            : ''
        };
      case purchasePending:
        return {
          title: 'Another customer started purchasing this vehicle',
          description: 'Sign up to be notified when it’s available.'
        };
      case unavailable &&
        !isWipReservedByUser &&
        errorReason === AvailabilityErrorReason.Consignment:
        return {
          title: `This vehicle is not available for sale in ${stateName}`,
          description: `Due to state title and registration requirements, we cannot sell this vehicle to ${stateName} residents. But don't fret, we've got many options to choose from.`
        };
      case unavailable && !isWipReservedByUser:
        return {
          title: 'This vehicle is no longer available',
          description: shouldShowDontFret
            ? 'Don’t fret, we’ve got many options to choose from.'
            : ''
        };

      default:
        return null;
    }
  }, [
    errorReason,
    isSold,
    isWipReservedByUser,
    otherUnavailable,
    purchasePending,
    shouldShowDontFret,
    stateName,
    unavailable
  ]);

  return {
    content,
    showCtaFooter:
      !isDealership &&
      !isSubscribedToNotifications &&
      !isInExclusivePreview &&
      (isSold ||
        purchasePending ||
        ((unavailable || otherUnavailable) && !isWipReservedByUser))
  };
}
